import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, UserPlus, Search, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg, money } from "@/lib/api";
import { EmptyState } from "@/components/Bits";
import JoinerFormDialog from "@/components/admin/JoinerFormDialog";

const Chips = ({ label, items, tone }) =>
  items?.length ? (
    <div className="flex flex-wrap items-center gap-1">
      <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 w-10">{label}</span>
      {items.map((m) => <span key={m} className={`text-xs rounded-md px-1.5 py-0.5 ${tone}`}>{m}</span>)}
    </div>
  ) : null;

const JoinerRow = ({ j, onEdit, onDelete, onReviewed }) => (
  <div data-testid={`admin-joiner-${j.id}`} className={`grid sm:grid-cols-[1fr_auto] gap-3 rounded-xl border px-4 py-3 ${j.self_registered ? "border-pink-500/30 bg-pink-500/[0.05]" : "border-white/5 bg-zinc-900/60"}`}>
    <div className="min-w-0 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <Link to={`/joiners/${j.id}`} className="font-semibold truncate hover:text-violet-300 transition-colors">{j.name}</Link>
        {j.self_registered && <span data-testid={`joiner-new-tag-${j.id}`} className="rounded-full bg-pink-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">New</span>}
        {j.self_registered && (
          <button type="button" data-testid={`joiner-mark-reviewed-${j.id}`} onClick={() => onReviewed(j)} className="inline-flex items-center gap-1 rounded-full border border-zinc-700 px-2 py-0.5 text-[11px] text-zinc-300 transition-colors hover:border-emerald-400/60 hover:text-emerald-300">
            <Check className="h-3 w-3" /> Mark reviewed
          </button>
        )}
      </div>
      <Chips label="GoM" items={j.gom_claims} tone="bg-amber-400/10 text-amber-200" />
      <Chips label="Fixed" items={j.fixed_claims} tone="bg-pink-500/10 text-pink-200" />
      <Chips label="Soft" items={j.soft_claims} tone="bg-sky-500/10 text-sky-200" />
      <Chips label="Open" items={j.open_claims} tone="bg-zinc-700/50 text-zinc-300" />
    </div>
    <div className="flex items-center gap-3 sm:justify-end">
      <span className="font-mono text-xs text-zinc-400">{j.claims_count} claims</span>
      <span className={`font-mono text-sm w-20 text-right ${j.balance > 0 ? "text-amber-400" : "text-emerald-400"}`}>{money(j.balance)}</span>
      <Button size="icon" variant="ghost" data-testid={`edit-joiner-${j.id}`} onClick={() => onEdit(j)}><Pencil className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" data-testid={`delete-joiner-${j.id}`} onClick={() => onDelete(j)}><Trash2 className="h-4 w-4" /></Button>
    </div>
  </div>
);

export default function ManageJoiners() {
  const [joiners, setJoiners] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    api.get("/joiners").then((r) => setJoiners(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const list = useMemo(
    () => joiners
      .filter((j) => j.name.toLowerCase().includes(q.toLowerCase()))
      .sort((a, b) => (b.self_registered ? 1 : 0) - (a.self_registered ? 1 : 0) || a.name.localeCompare(b.name)),
    [joiners, q]
  );
  const newCount = joiners.filter((j) => j.self_registered).length;

  const reviewed = async (j) => {
    try {
      await api.patch(`/joiners/${j.id}/reviewed`);
      toast.success(`${j.name} marked as reviewed`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (j) => {
    if (!window.confirm(`Delete ${j.name}?`)) return;
    try {
      await api.delete(`/joiners/${j.id}`);
      toast.success("Joiner deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="manage-joiners">
      <div className="flex flex-col sm:flex-row gap-2">
        <Button data-testid="add-joiner-button" onClick={() => setEditing({})} className="rounded-xl bg-gradient-to-r from-violet-600 to-pink-600 text-white font-semibold hover:opacity-90">
          <UserPlus className="h-4 w-4 mr-1.5" /> Add Joiner
        </Button>
        <div className="relative sm:ml-auto sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input data-testid="manage-joiners-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search joiners" className="pl-9 bg-zinc-900 border-zinc-800" />
        </div>
      </div>
      {newCount > 0 && <div data-testid="new-joiners-banner" className="mt-4 rounded-xl border border-pink-500/30 bg-pink-500/[0.06] px-4 py-2.5 text-sm text-pink-100">{newCount} new {newCount === 1 ? "joiner" : "joiners"} added themselves from a claim — review below.</div>}
      <div className="mt-4 space-y-2" data-testid="admin-joiners-list">
        {list.length === 0 && <EmptyState title={joiners.length ? "No match" : "No joiners yet"} hint={joiners.length ? undefined : "Use Add Joiner to create the first one."} />}
        {list.map((j) => <JoinerRow key={j.id} j={j} onEdit={setEditing} onDelete={remove} onReviewed={reviewed} />)}
      </div>
      {editing && <JoinerFormDialog joiner={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
